import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import {
    ArrowUpRight,
    ArrowRight,
    Globe2,
    Factory,
    Compass,
    Search,
    ClipboardCheck,
    Target,
    Briefcase,
    Handshake,
    Rocket,
    GaugeCircle,
} from "lucide-react";
import { Reveal, Eyebrow, EASE } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import { PARTNER_LOGOS } from "@/data/logos";
import { useLang } from "@/i18n/LanguageContext";
import { TESTIMONIALS } from "@/data/testimonials";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOLUTION_LINKS = ["/solutions/developpement-international", "/solutions/programmes-appui-formation", "/solutions/export360", "/solutions/implantation-rh-international"];
const METHOD_ICONS = [ClipboardCheck, Target, Search, Briefcase, Rocket, GaugeCircle];

function MaskedLine({ children, index }) {
    return (
        <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span
                className="block will-change-transform"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.25 + index * 0.14, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const backdropY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
    const { t, prefix } = useLang();
    const zones = t("home.zones.zones");

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-[92vh] flex flex-col justify-center overflow-hidden bg-white pt-28 pb-16">
            <motion.span
                aria-hidden="true"
                style={{ y: backdropY, opacity: fade }}
                className="pointer-events-none select-none absolute -right-8 top-1/2 -translate-y-1/2 font-display font-bold text-[26vw] leading-none tracking-tighter text-outline hidden md:block"
            >
                WEYB
            </motion.span>

            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-semibold text-gray-500 mb-10"
                >
                    <span className="h-2 w-2 bg-weyb-yellow" aria-hidden="true" />
                    {t("home.hero.eyebrow")}
                </motion.p>

                <h1 data-testid="hero-title" className="font-display font-light tracking-tight leading-[1.02] text-weyb-ink text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
                    <MaskedLine index={0}>{t("home.hero.line1")}</MaskedLine>
                    <MaskedLine index={1}>
                        {t("home.hero.line2a")}
                        <span className="relative inline-block">
                            {t("home.hero.line2b")}
                            <span className="absolute left-0 -bottom-1 md:-bottom-2 h-[3px] w-full bg-weyb-yellow" aria-hidden="true" />
                        </span>
                        {t("home.hero.line2c")}
                    </MaskedLine>
                    <MaskedLine index={2}>{t("home.hero.line3")}</MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                    className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-gray-600"
                >
                    {t("home.hero.intro")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        href={CAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-cta-booking"
                        className="group inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors"
                    >
                        {t("home.hero.ctaPrimary")}
                        <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <a
                        href="#solutions"
                        data-testid="hero-cta-solutions"
                        className="inline-flex items-center gap-2 border border-gray-300 text-weyb-ink hover:border-weyb-yellow px-7 py-4 rounded-sm text-sm font-medium transition-colors"
                    >
                        {t("home.hero.ctaSecondary")}
                        <ArrowRight size={16} />
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.3 }}
                    className="mt-16 pt-8 border-t border-gray-100 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.18em] text-gray-400 font-semibold"
                >
                    {zones.map((z, i) => (
                        <span key={z} data-testid={`hero-zone-${i}`}>{z}</span>
                    ))}
                    <span className="text-gray-300 normal-case tracking-normal font-normal">
                        {t("home.hero.zoneNote")}
                    </span>
                </motion.div>
            </div>
        </section>
    );
}

function Solutions() {
    const { t, prefix } = useLang();
    const cards = t("home.solutions.cards");

    return (
        <section id="solutions" data-testid="solutions-section" className="py-24 md:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("home.solutions.eyebrowIndex")}>{t("home.solutions.eyebrow")}</Eyebrow>
                <Reveal>
                    <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink max-w-3xl leading-tight">
                        {t("home.solutions.title")}
                    </h2>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {cards.map((s, i) => (
                        <Reveal key={s.num} delay={i * 0.1}>
                            <article
                                data-testid={`solution-card-${s.num}`}
                                className="group relative h-full bg-white border border-gray-200 rounded-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_rgba(10,15,22,0.08)] overflow-hidden"
                            >
                                <span className="absolute top-0 left-0 h-[3px] w-0 bg-weyb-yellow transition-all duration-500 group-hover:w-full" aria-hidden="true" />
                                <span className="font-display text-sm font-medium text-weyb-yellow">{s.num}</span>
                                <h3 className="mt-4 font-display text-xl md:text-2xl font-medium text-weyb-ink tracking-tight">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-gray-600">{s.value}</p>
                                <ul className="mt-6 space-y-2.5">
                                    {s.items.map((item) => (
                                        <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                                            <span className="mt-[7px] h-1 w-1 shrink-0 bg-weyb-yellow" aria-hidden="true" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={`${prefix}${SOLUTION_LINKS[i]}`}
                                    data-testid={`solution-cta-${s.num}`}
                                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-weyb-ink hover:text-weyb-yellow-dark transition-colors"
                                >
                                    {s.cta}
                                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                                </Link>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Institutions() {
    const { t, prefix } = useLang();
    const blocks = t("home.institutions.blocks");
    const stats = t("home.institutions.stats");

    return (
        <section data-testid="institutions-section" className="py-24 md:py-32 bg-[#F9FAFB] border-y border-gray-100">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("home.institutions.eyebrowIndex")}>{t("home.institutions.eyebrow")}</Eyebrow>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-end">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink leading-tight">
                            {t("home.institutions.title")}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                            {t("home.institutions.text")}
                        </p>
                    </Reveal>
                </div>

                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
                    {blocks.map((b, i) => (
                        <Reveal key={b.title} delay={i * 0.06} className="h-full">
                            <div data-testid={`institutions-block-${i}`} className="group h-full bg-white hover:bg-weyb-ink p-6 transition-colors duration-300">
                                <span className="font-display text-sm font-medium text-weyb-yellow">{String(i + 1).padStart(2, "0")}</span>
                                <h3 className="mt-4 font-display text-base font-semibold text-weyb-ink group-hover:text-white tracking-tight transition-colors">
                                    {b.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-gray-600 group-hover:text-white/60 transition-colors">{b.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15}>
                    <div className="mt-10 flex flex-wrap items-center gap-x-12 gap-y-6">
                        {stats.map((s, i) => (
                            <div key={s.label} data-testid={`institutions-stat-${i}`} className="flex items-baseline gap-3">
                                <span className="font-display text-4xl md:text-5xl font-light text-weyb-ink">{s.value}</span>
                                <span className="text-xs uppercase tracking-[0.16em] font-semibold text-gray-500 max-w-[140px]">{s.label}</span>
                            </div>
                        ))}
                        <Link
                            to={`${prefix}/solutions/programmes-appui-formation`}
                            data-testid="institutions-cta"
                            className="group inline-flex items-center gap-2 bg-weyb-ink text-white hover:bg-weyb-yellow hover:text-weyb-ink px-7 py-4 rounded-sm text-sm font-semibold transition-colors ml-auto"
                        >
                            {t("home.institutions.cta")}
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

function Method() {
    const { t } = useLang();
    const steps = t("home.method.steps");

    return (
        <section data-testid="method-section" className="py-24 md:py-32 bg-[#F9FAFB] border-y border-gray-100">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("home.method.eyebrowIndex")}>{t("home.method.eyebrow")}</Eyebrow>
                <Reveal>
                    <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink max-w-3xl leading-tight">
                        {t("home.method.title")}
                    </h2>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
                    {steps.map((step, i) => {
                        const Icon = METHOD_ICONS[i];
                        return (
                            <Reveal key={step.num} delay={i * 0.06} className="h-full">
                                <div
                                    data-testid={`method-step-${step.num}`}
                                    className="group h-full bg-[#F9FAFB] hover:bg-white p-8 transition-colors duration-300"
                                >
                                    <div className="flex items-baseline justify-between">
                                        <span className="font-display text-4xl font-light text-weyb-yellow">{step.num}</span>
                                        <Icon size={20} strokeWidth={1.5} className="text-gray-300 group-hover:text-weyb-ink transition-colors" aria-hidden="true" />
                                    </div>
                                    <h3 className="mt-6 font-display text-lg font-semibold uppercase tracking-[0.08em] text-weyb-ink">
                                        {step.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-gray-600">{step.desc}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function Export360() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);
    const { t } = useLang();
    const features = t("home.export360.features");

    return (
        <section ref={ref} data-testid="export360-section" className="py-24 md:py-32 bg-weyb-ink text-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    <div>
                        <Eyebrow index={t("home.export360.eyebrowIndex")} dark>{t("home.export360.eyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight">
                                {t("home.export360.titleA")}
                                <span className="text-weyb-yellow">{t("home.export360.titleB")}</span>
                                {t("home.export360.titleC")}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
                                {t("home.export360.desc")}
                            </p>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <div className="mt-8 flex flex-wrap gap-2.5">
                                {features.map((f) => (
                                    <span
                                        key={f}
                                        className="text-xs font-medium text-white/70 border border-white/15 hover:border-weyb-yellow hover:text-weyb-yellow transition-colors px-3.5 py-2 rounded-sm"
                                    >
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.22} className="mt-10">
                            <a
                                href="https://export360.fr/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="export360-cta"
                                className="group inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors"
                            >
                                {t("home.export360.cta")}
                                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </Reveal>
                    </div>

                    <Reveal delay={0.15} className="relative">
                        <div className="relative">
                            <span className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-weyb-yellow" aria-hidden="true" />
                            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-sm bg-white">
                                <img
                                    src="/assets/export360-screenshot.webp"
                                    alt={t("home.export360.visualAlt")}
                                    data-testid="export360-visual"
                                    loading="lazy"
                                    className="block w-full h-auto"
                                />
                            </motion.div>
                            <span className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-weyb-yellow" aria-hidden="true" />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function ZonesSecteurs() {
    const { t } = useLang();
    const zones = t("home.zones.zones");
    const sectors = t("home.zones.sectors");

    return (
        <section data-testid="zones-secteurs-section" className="py-24 md:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("home.zones.eyebrowIndex")}>{t("home.zones.eyebrow")}</Eyebrow>
                <Reveal>
                    <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink max-w-3xl leading-tight">
                        {t("home.zones.title")}
                    </h2>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    <Reveal>
                        <article data-testid="zones-block" className="group relative h-full min-h-[380px] overflow-hidden rounded-sm border border-gray-200">
                            <img
                                src="/assets/terrain-salon-amdie.jpg"
                                alt={t("home.zones.zonesImgAlt")}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-weyb-ink/70" aria-hidden="true" />
                            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
                                <Globe2 size={22} strokeWidth={1.5} className="text-weyb-yellow mb-4" aria-hidden="true" />
                                <h3 className="font-display text-2xl font-medium text-white">{t("home.zones.zonesTitle")}</h3>
                                <div className="mt-5 flex flex-wrap gap-2.5">
                                    {zones.map((z, i) => (
                                        <span key={z} data-testid={`zone-tag-${i}`} className="text-sm font-semibold text-weyb-ink bg-weyb-yellow px-4 py-2 rounded-sm">
                                            {z}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-5 text-sm text-white/60 max-w-md">
                                    {t("home.zones.zonesNote")}
                                </p>
                            </div>
                        </article>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <article data-testid="secteurs-block" className="h-full min-h-[380px] bg-white border border-gray-200 rounded-sm p-8 md:p-10 flex flex-col">
                            <Factory size={22} strokeWidth={1.5} className="text-weyb-yellow mb-4" aria-hidden="true" />
                            <h3 className="font-display text-2xl font-medium text-weyb-ink">{t("home.zones.sectorsTitle")}</h3>
                            <ul className="mt-6 flex-1 divide-y divide-gray-100">
                                {sectors.map((s, i) => (
                                    <li key={s} data-testid={`sector-${i}`} className="group flex items-center justify-between py-4">
                                        <span className="font-display text-lg md:text-xl font-medium text-weyb-ink group-hover:translate-x-1 transition-transform">
                                            {s}
                                        </span>
                                        <span className="font-display text-xs text-gray-300">0{i + 1}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-6 text-sm text-gray-500">
                                {t("home.zones.sectorsNote")}
                            </p>
                        </article>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function Experiences() {
    const { t } = useLang();

    return (
        <section data-testid="experiences-section" className="py-24 md:py-32 bg-[#F9FAFB] border-y border-gray-100">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("home.experiences.eyebrowIndex")}>{t("home.experiences.eyebrow")}</Eyebrow>
                <Reveal>
                    <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink max-w-3xl leading-tight">
                        {t("home.experiences.title")}
                    </h2>
                </Reveal>
                <Reveal delay={0.08}>
                    <p className="mt-5 text-sm text-gray-500 max-w-2xl">
                        {t("home.experiences.note")}
                    </p>
                </Reveal>

                <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
                    {PARTNER_LOGOS.map((logo, i) => (
                        <Reveal key={logo.name} delay={(i % 4) * 0.05} className="h-full">
                            <div
                                data-testid={`partner-logo-${logo.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                title={logo.name}
                                className={`group h-full ${logo.darkBg ? "bg-[#0E0E18]" : "bg-white"} flex items-center justify-center p-6 min-h-[110px]`}
                            >
                                <img
                                    src={logo.src}
                                    alt={`Logo ${logo.name}`}
                                    loading="lazy"
                                    className={`${logo.size === "sq" ? "h-9" : "h-11"} w-auto max-w-[150px] object-contain grayscale opacity-50 transition-all duration-500 group-hover:grayscale-0 group-hover:opacity-100`}
                                />
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Testimonials() {
    const { t } = useLang();

    return (
        <section data-testid="testimonials-section" className="py-24 md:py-32 bg-white border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("home.testimonials.eyebrowIndex")}>{t("home.testimonials.eyebrow")}</Eyebrow>
                <Reveal>
                    <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink max-w-3xl leading-tight">
                        {t("home.testimonials.title")}
                    </h2>
                </Reveal>
                <Reveal delay={0.08}>
                    <p className="mt-5 text-sm text-gray-500 max-w-2xl">
                        {t("home.testimonials.note")}
                    </p>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {TESTIMONIALS.map((tm, i) => (
                        <Reveal key={tm.name} delay={(i % 2) * 0.08} className="h-full">
                            <figure
                                data-testid={`testimonial-${i}`}
                                className="h-full flex flex-col bg-[#F9FAFB] border border-gray-200 hover:border-weyb-yellow rounded-sm p-8 md:p-10 transition-colors duration-300"
                            >
                                <span aria-hidden="true" className="font-display text-6xl leading-[0.4] text-weyb-yellow select-none">&ldquo;</span>
                                <blockquote lang={tm.lang} className="mt-6 flex-1 text-sm md:text-[15px] leading-relaxed text-weyb-ink/80">
                                    {tm.quote}
                                </blockquote>
                                <figcaption className="mt-7 pt-5 border-t border-gray-200 flex items-center gap-4">
                                    <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-weyb-ink text-weyb-yellow font-display text-sm font-semibold">
                                        {tm.name.split(" ").map((n) => n[0]).join("")}
                                    </span>
                                    <div>
                                        <p className="font-display font-semibold text-weyb-ink text-sm">{tm.name}</p>
                                        <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-[0.1em]">{tm.role}</p>
                                    </div>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Founder() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [30, -30]);
    const { t, prefix } = useLang();
    const stats = t("home.founder.stats");

    return (
        <section ref={ref} data-testid="founder-section" className="py-24 md:py-32 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    <Reveal className="lg:col-span-5 relative">
                        <div className="relative max-w-md">
                            <span className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-weyb-yellow z-10" aria-hidden="true" />
                            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-sm bg-[#F9FAFB]">
                                <img
                                    src="/assets/saber-portrait.webp"
                                    alt={t("home.founder.portraitAlt")}
                                    data-testid="founder-portrait"
                                    loading="lazy"
                                    className="w-full aspect-[4/5] object-cover object-[62%_15%]"
                                />
                            </motion.div>
                            <span className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-weyb-yellow" aria-hidden="true" />
                        </div>
                    </Reveal>

                    <div className="lg:col-span-7">
                        <Eyebrow index={t("home.founder.eyebrowIndex")}>{t("home.founder.eyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink leading-tight">
                                {t("home.founder.name")}
                                <span className="block mt-2 text-lg md:text-xl font-medium text-gray-500 tracking-normal">
                                    {t("home.founder.role")}
                                </span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-base md:text-lg leading-relaxed text-gray-600 max-w-xl">
                                {t("home.founder.bio")}
                            </p>
                        </Reveal>

                        <div className="mt-10 grid grid-cols-2 gap-px bg-gray-200 border border-gray-200 max-w-xl">
                            {stats.map((stat, i) => (
                                <Reveal key={stat.label} delay={0.1 + i * 0.06} className="h-full">
                                    <div data-testid={`founder-stat-${i}`} className="h-full bg-white p-6">
                                        <span className="font-display text-3xl font-light text-weyb-yellow">{stat.value}</span>
                                        <p className="mt-2 text-xs leading-relaxed text-gray-500 uppercase tracking-[0.08em]">{stat.label}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={0.25} className="mt-10">
                            <Link
                                to={`${prefix}/a-propos`}
                                data-testid="founder-cta"
                                className="group inline-flex items-center gap-2 border border-weyb-ink text-weyb-ink hover:bg-weyb-ink hover:text-white px-7 py-4 rounded-sm text-sm font-semibold transition-colors"
                            >
                                {t("home.founder.cta")}
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Analyses() {
    const [articles, setArticles] = useState([]);
    const { t, prefix } = useLang();
    useEffect(() => {
        axios.get(`${API}/articles`).then((res) => setArticles(res.data)).catch(() => setArticles([]));
    }, []);

    return (
        <section data-testid="analyses-section" className="py-24 md:py-32 bg-[#F9FAFB] border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Eyebrow index={t("home.analyses.eyebrowIndex")}>{t("home.analyses.eyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink leading-tight">
                                {t("home.analyses.title")}
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <Link
                            to={`${prefix}/analyses`}
                            data-testid="analyses-cta-all"
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-weyb-ink hover:text-weyb-yellow-dark transition-colors"
                        >
                            {t("home.analyses.ctaAll")}
                            <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </Reveal>
                </div>

                {articles.length === 0 ? (
                    <Reveal delay={0.15}>
                        <div data-testid="analyses-empty" className="mt-14 border border-dashed border-gray-300 rounded-sm p-12 md:p-16 text-center bg-white">
                            <Compass size={24} strokeWidth={1.5} className="mx-auto text-weyb-yellow" aria-hidden="true" />
                            <p className="mt-4 font-display text-xl font-medium text-weyb-ink">
                                {t("home.analyses.emptyTitle")}
                            </p>
                            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                                {t("home.analyses.emptyText")}
                            </p>
                        </div>
                    </Reveal>
                ) : (
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {articles.slice(0, 3).map((a, i) => (
                            <Reveal key={a.id} delay={i * 0.08}>
                                <Link to={`${prefix}/analyses/${a.slug}`} data-testid={`article-card-${i}`} className="block group h-full bg-white border border-gray-200 rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-[0_24px_48px_rgba(10,15,22,0.08)] transition-all duration-300">
                                    {a.image && (
                                        <div className="aspect-[16/9] overflow-hidden">
                                            <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        </div>
                                    )}
                                    <div className="p-7">
                                        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-weyb-yellow-dark">{a.category}</span>
                                        <h3 className="mt-3 font-display text-lg font-medium text-weyb-ink leading-snug">{a.title}</h3>
                                        <p className="mt-3 text-sm text-gray-600 leading-relaxed">{a.excerpt}</p>
                                        <p className="mt-4 text-xs text-gray-400">{new Date(a.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function FinalCta() {
    const { t, prefix } = useLang();

    return (
        <section data-testid="final-cta-section" className="relative py-28 md:py-40 bg-weyb-ink text-white overflow-hidden">
            <span aria-hidden="true" className="pointer-events-none select-none absolute -left-10 bottom-0 font-display font-bold text-[22vw] leading-none tracking-tighter text-outline-light">
                360°
            </span>
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                <Reveal>
                    <p className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-semibold text-weyb-yellow mb-8">
                        <Handshake size={16} aria-hidden="true" />
                        {t("home.finalCta.eyebrow")}
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] max-w-4xl">
                        {t("home.finalCta.title")}
                    </h2>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-6 text-white/60 text-base md:text-lg max-w-xl leading-relaxed">
                        {t("home.finalCta.desc")}
                    </p>
                </Reveal>
                <Reveal delay={0.24} className="mt-10 flex flex-wrap gap-4">
                    <a
                        href={CAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="final-cta-booking"
                        className="group inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-8 py-4 rounded-sm text-sm font-semibold transition-colors"
                    >
                        {t("home.finalCta.ctaPrimary")}
                        <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <Link
                        to={`${prefix}/contact`}
                        data-testid="final-cta-contact"
                        className="inline-flex items-center gap-2 border border-white/20 text-white hover:border-weyb-yellow hover:text-weyb-yellow px-8 py-4 rounded-sm text-sm font-medium transition-colors"
                    >
                        {t("home.finalCta.ctaSecondary")}
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

// Partage social unique de la homepage (LinkedIn, WhatsApp, Messenger, X) — en anglais,
// indépendant du H1 et des balises SEO FR/EN visibles.
const SOCIAL_TITLE = "WEYB - International Development, from Strategy to Execution";
const SOCIAL_DESCRIPTION =
    "International development consulting firm helping companies and organizations turn international strategy into action.";

export default function Home() {
    usePageMeta(
        { fr: "Accompagnement export & développement international | WEYB", en: "WEYB — International development, from strategy to execution" },
        { fr: "WEYB accompagne PME, dirigeants et équipes export dans leur développement international : stratégie export, études de marché, prospection B2B, salons et implantation.", en: "International development consultancy: export strategy, support programmes, training, B2B prospecting, trade fairs, setup — Europe, Africa, MENA." },
        { title: SOCIAL_TITLE, description: SOCIAL_DESCRIPTION }
    );
    return (
        <>
            <Hero />
            <Marquee />
            <Solutions />
            <Institutions />
            <Experiences />
            <Method />
            <Export360 />
            <ZonesSecteurs />
            <Testimonials />
            <Founder />
            <Analyses />
            <FinalCta />
        </>
    );
}
